import { Component, input, OnChanges, OnInit, output, signal, SimpleChanges } from '@angular/core';

@Component({
    selector: 'app-avatar-uploader',
    imports: [],
    templateUrl: './avatar-uploader.component.html',
    styleUrl: './avatar-uploader.component.scss',
})
export class AvatarUploaderComponent implements OnInit, OnChanges {
    /** Default avatar image used when provided URL fails to load */
    private static readonly DEFAULT_AVATAR_IMAGE_URL = '/default_profile.png';
    /** Allowed profile image MIME types. */
    private static readonly ALLOWED_IMAGE_MIME_TYPES = new Set([
        'image/jpeg',
        'image/jpg',
        'image/png',
    ]);

    /** Input image url to be displayed in the avatar */
    public readonly imageUrl = input<string>();

    /** Variant of the uploader: 'default' (centered with info) or 'sidebar' (minimal) */
    public readonly variant = input<'default' | 'sidebar'>('default');

    /** Controls whether the upload action is enabled */
    public readonly isEditable = input<boolean>(true);

    /** Emits the selected File when a user uploads a new image */
    public readonly imageChange = output<File>();
    /** Emits when a user selects an unsupported file type. */
    public readonly invalidFileSelected = output<void>();

    /** Current image preview URL (either from input or newly uploaded) */
    protected readonly imagePreviewUrl = signal<string | null>(null);

    /**
     * @description Initializes the image preview if an initial URL is provided
     * @returns void
     */
    public ngOnInit(): void {
        this.syncImagePreview();
    }

    /**
     * @description Updates preview when the bound image URL changes after initialization
     * @param changes - Angular input changes map
     * @returns void
     */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['imageUrl']) {
            this.syncImagePreview();
        }
    }

    /**
     * @description Processes the file input change event, emits the file, and updates the local preview
     * @param event - The browser file input change event
     * @returns void
     */
    public onFileChange(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];

        if (!file) {
            return;
        }

        if (!AvatarUploaderComponent.ALLOWED_IMAGE_MIME_TYPES.has(file.type)) {
            this.invalidFileSelected.emit();
            input.value = '';
            return;
        }

        this.imageChange.emit(file);

        const reader = new FileReader();
        reader.onload = (): void => {
            this.imagePreviewUrl.set(reader.result as string);
        };
        reader.readAsDataURL(file);
    }

    /**
     * @description Handles image load errors and falls back to default profile image
     * @returns void
     */
    public onImageError(): void {
        if (this.imagePreviewUrl() !== AvatarUploaderComponent.DEFAULT_AVATAR_IMAGE_URL) {
            this.imagePreviewUrl.set(AvatarUploaderComponent.DEFAULT_AVATAR_IMAGE_URL);
            return;
        }

        this.imagePreviewUrl.set(null);
    }

    /**
     * @description Synchronizes local preview state from current image URL input
     * @returns void
     */
    private syncImagePreview(): void {
        const imageUrl = this.imageUrl();

        if (!imageUrl) {
            this.imagePreviewUrl.set(AvatarUploaderComponent.DEFAULT_AVATAR_IMAGE_URL);
            return;
        }

        if (imageUrl === AvatarUploaderComponent.DEFAULT_AVATAR_IMAGE_URL) {
            this.imagePreviewUrl.set(imageUrl);
            return;
        }

        // Show default immediately so UI does not wait for remote image load.
        this.imagePreviewUrl.set(AvatarUploaderComponent.DEFAULT_AVATAR_IMAGE_URL);

        const preloadedImage = new Image();
        preloadedImage.onload = (): void => {
            this.imagePreviewUrl.set(imageUrl);
        };
        preloadedImage.onerror = (): void => {
            this.imagePreviewUrl.set(AvatarUploaderComponent.DEFAULT_AVATAR_IMAGE_URL);
        };
        preloadedImage.src = imageUrl;
    }
}
